import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import type { BravePresetId, BravePresetResponse, BravePresetSourceMode } from '@pokerroom/types';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
import { api } from '../../lib/api';
import { braveCutoff, bravePresetCopy } from './BraveRankingPage.copy';

/** Mounted inside the existing session/venue workspace; never shares official save state. */
export function BravePresetSection({
  language,
  onDenied,
}: {
  language: string;
  onDenied: (error: unknown) => boolean;
}) {
  const { t } = useTranslation();
  const c = bravePresetCopy(language, t);
  const revision = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const [period, setPeriod] = useState('');
  const [request, setRequest] = useState<{ period?: string; sequence: number }>({ sequence: 0 });
  const [sourceMode, setSourceMode] = useState<BravePresetSourceMode>('TOURNAMENT');
  const crm = sourceMode === 'TOURNAMENT_WITH_CRM_OPENING';
  const [preset, setPreset] = useState<BravePresetId>('MONTH');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const deny = useRef(onDenied);
  deny.current = onDenied;
  const query = useQuery({
    queryKey: ['brave-preset', revision, venue, request, sourceMode],
    queryFn: ({ signal }) =>
      api.get<BravePresetResponse>('/brave-ranking/preset-previews', {
        signal,
        params: {
          ...(request.period ? { period: request.period } : {}),
          ...(crm ? { sourceMode } : {}),
        },
      }),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const loading = query.isFetching;
  const data = loading || query.isError ? null : (query.data ?? null);
  useEffect(() => {
    if (query.data && !query.isFetching) setPeriod(query.data.period);
  }, [query.data, query.isFetching]);
  useEffect(() => {
    setError(query.error ? (deny.current(query.error) ? 'denied' : 'error') : '');
  }, [query.error]);
  const unavailable =
    data && 'crmSource' in data && ['unavailable', 'unsupported'].includes(data.crmSource.status);
  const view = unavailable ? undefined : data?.views.find((v) => v.id === preset);
  const rows =
    view?.rows.filter((r) => r.nickname.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ??
    [];
  return (
    <section className="brave-presets" aria-label={crm ? c.crmTitle : c.title}>
      <h2>{crm ? c.crmTitle : c.title}</h2>
      <p>{crm ? c.crmIntro : c.intro}</p>
      <label>
        {c.source}
        <select
          aria-label={c.source}
          value={sourceMode}
          onChange={(e) => {
            setSearch('');
            setPreset('MONTH');
            setRequest((r) => ({ ...r, sequence: r.sequence + 1 }));
            setSourceMode(e.target.value as BravePresetSourceMode);
          }}
        >
          <option value="TOURNAMENT">{c.tournament}</option>
          <option value="TOURNAMENT_WITH_CRM_OPENING">{c.crm}</option>
        </select>
      </label>
      {crm && <p>{c.monthOnly}</p>}
      <div className="brave-controls">
        <label>
          {c.period}
          <input
            type="month"
            value={period}
            onChange={(e) => {
              const value = e.target.value;
              setPeriod(value);
              setSearch('');
              setRequest((r) => ({ period: value || undefined, sequence: r.sequence + 1 }));
            }}
          />
        </label>
        <div className="brave-preset-buttons" aria-label={crm ? c.crmTitle : c.title}>
          {(['THROUGH_10', 'THROUGH_20', 'MONTH'] as const).map((id, i) => (
            <button
              type="button"
              key={id}
              disabled={crm && id !== 'MONTH'}
              aria-pressed={preset === id}
              onClick={() => setPreset(id)}
            >
              {[c.through10, c.through20, c.month][i]}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={() => setRequest((r) => ({ ...r, sequence: r.sequence + 1 }))}
        >
          {c.retry}
        </button>
      </div>
      {loading && <p role="status">{c.loading}</p>}
      {error && <p role="alert">{error === 'denied' ? c.denied : c.error}</p>}
      {data && 'crmSource' in data && (
        <>
          {data.crmSource.exportedAt && (
            <p>
              {c.exportedAt}: {braveCutoff(data.crmSource.exportedAt)}
            </p>
          )}
          {['unavailable', 'unsupported'].includes(data.crmSource.status) && (
            <p role="alert">
              {data.crmSource.status === 'unavailable' ? c.unavailable : c.unsupported}
            </p>
          )}
        </>
      )}
      {view && (
        <>
          <p>
            {view.startBusinessDate} – {view.endBusinessDate} · {c[view.status]}
          </p>
          <p>
            {c.asOf}: {braveCutoff(data!.asOf)}
          </p>
          {view.quality === 'incomplete' && (
            <p role="alert">
              {c.incomplete} ({view.issues.length})
            </p>
          )}
          {crm &&
            [...new Set(view.issues.map((i) => i.code))]
              .filter((code) => code in c)
              .map((code) => (
                <p role="alert" key={code}>
                  {c[code as keyof typeof c]}
                </p>
              ))}
          <label>
            {c.search}
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <div className="brave-table-scroll">
            <table className={crm ? 'brave-crm-table' : undefined}>
              <thead>
                <tr>
                  <th>{c.rank}</th>
                  <th>{c.nickname}</th>
                  {crm ? (
                    <>
                      <th>{c.tournamentPoints}</th>
                      <th>{c.crmOpening}</th>
                      <th>{c.referenceTotal}</th>
                    </>
                  ) : (
                    <>
                      <th>{c.points}</th>
                      <th>{c.attainment}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.memberId}>
                    <td>{'rank' in r ? r.rank : r.position}</td>
                    <td>{r.nickname}</td>
                    {'crmOpeningPoints' in r ? (
                      <>
                        <td>{r.tournamentPoints}</td>
                        <td>{r.crmOpeningPoints}</td>
                        <td>{r.points}</td>
                      </>
                    ) : (
                      <>
                        <td>{r.points}</td>
                        <td>{braveCutoff(r.attainedAt)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && view.quality === 'complete' && <p>{c.empty}</p>}
        </>
      )}
    </section>
  );
}
